import { ColumnDef } from '@tanstack/react-table';
import { DataTableColumnHeader } from '@/components/core';
import { PROFILE_SOURCES } from '../../../shared/candidate-form-schema';
import {
  CommentCell,
  namesInJson,
  sourceColumnFilter,
} from '../../../shared/components/candidate-cells';
import { ConditionalOffer } from '../../types/offer.types';

interface CreateOfferTableColumnsProps {
  t: (key: string) => string;
}

/**
 * Offers (HR Module Prototype `hiringPage("offers")`): Name, Phone number, Email, Interview panel, Years of
 * experience, Designation, Source, Internal referral, Unit, Comments. Read-only besides create, as before;
 * the prototype's CV column waits for CV upload (not stored yet).
 */
export const createOfferTableColumns = ({
  t,
}: CreateOfferTableColumnsProps): ColumnDef<ConditionalOffer>[] => [
  {
    // Shows the full name; sorts and filters by first name.
    id: 'FirstName',
    meta: { filter: { type: 'text' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('NAME')} />,
    accessorFn: (row) => `${row.FirstName} ${row.LastName}`,
    cell: ({ getValue }) => <span className="font-medium">{getValue<string>()}</span>,
  },
  {
    id: 'PhoneNumber',
    meta: { filter: { type: 'text' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('PHONE_NUMBER')} />,
    accessorFn: (row) => row.PhoneNumber,
    cell: ({ getValue }) => <span className="whitespace-nowrap tabular-nums">{getValue<string>()}</span>,
  },
  {
    id: 'Email',
    meta: { filter: { type: 'text' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('EMAIL')} />,
    accessorFn: (row) => row.Email,
  },
  {
    id: 'InterviewPanel',
    // Names read from InterviewPanelJson; not a field the server can sort on.
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('INTERVIEW_PANEL')} />,
    accessorFn: (row) => namesInJson(row.InterviewPanelJson).join(', ') || '—',
  },
  {
    id: 'YearsOfExperience',
    meta: { filter: { type: 'number' }, numeric: true },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('YEARS_OF_EXPERIENCE')} />
    ),
    accessorFn: (row) => row.YearsOfExperience,
  },
  {
    id: 'Designation',
    meta: { filter: { type: 'text' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('DESIGNATION')} />,
    accessorFn: (row) => row.Designation,
  },
  {
    id: 'Source',
    meta: { filter: sourceColumnFilter(PROFILE_SOURCES) },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('SOURCE')} />,
    accessorFn: (row) => row.Source,
  },
  {
    id: 'InternalReferral',
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('INTERNAL_REFERRAL')} />,
    accessorFn: (row) => namesInJson(row.InternalReferralJson).join(', ') || '—',
  },
  {
    id: 'HiringDepartmentName',
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('UNIT')} />,
    accessorFn: (row) => row.HiringDepartmentName || '—',
  },
  {
    id: 'Comment',
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('COMMENT')} />,
    accessorFn: (row) => row.Comment ?? '',
    cell: ({ getValue }) => <CommentCell text={getValue<string>()} />,
  },
];
